<template>
    <section :class="['register', 'widget', { 'widget--disabled': !isRegisterWidgetEnabled }]">
        <header>
            <h1 class="widget__title">
                {{ $t('registerUsername.heading') }}
            </h1>
        </header>

        <form @submit.prevent="checkRegulation">
            <username :is-disabled="isLoading"
                :prepopulatedUsername="prepopulatedUserInfo.username"
            />
            <section class="register__widget-row">
                <email :is-disabled="isLoading"
                    :email="prepopulatedUserInfo.email"
                    registration-page
                    :validateOnMount="true"
                />
            </section>
            <country-state-dropdown ref="countryStateInput"
                v-model="selectedCountry"
                :prepopulatedCountry="prepopulatedUserInfo.country"
            />
            <date-of-birth :is-disabled="isLoading"
                :prepopulatedDateOfBirth="prepopulatedUserInfo.dob"
            />

            <div class="warning__age-restrictions warning__incl-icon">
                <i class="warning__icon icon-alert" />
                <p class="warning__text">{{ $t('registerUsername.ageRestrictions') }}</p>
            </div>

            <label :class="['label label--flexed label--light', { 'label--disabled': isLoading }]">
                <input v-model="form.isMailingCheckboxTicked"
                    :disabled="isLoading"
                    type="checkbox"
                >
                <translation-string :translation="$t('registerUsername.MailingOptin')" />
            </label>

            <label :class="['label label--flexed label--light', { 'label--disabled': isCheckboxDisabled }]">
                <input id="checkbox-terms"
                    v-model="form.isTermsCheckboxTicked"
                    :disabled="isCheckboxDisabled"
                    type="checkbox"
                >

                <span>
                    {{ $t('registerUsername.byRegisteringYouAgree') }}

                    <a :class="['generic-link', { 'register-widget-link--disabled': isCheckboxDisabled }]"
                        target="_blank"
                        :href="termsAndConditionsLink"
                    >
                        {{ $t('registerUsername.terms') }}
                    </a>.

                    {{ $t('registerUsername.howWeCollectData') }}

                    <a :class="['generic-link', { 'register-widget-link--disabled': isCheckboxDisabled }]"
                        target="_blank"
                        :href="privacyPolicyLink"
                    >
                        {{ $t('registerUsername.dataPolicy') }}
                    </a>.
                </span>
            </label>

            <button id="button-register"
                :class="['widget__button', { 'widget__button--disabled': isButtonDisabled }]"
                @click="checkRegulation"
            >
                {{ $t('registerUsername.registerNow') }}
            </button>

            <loading-spinner v-if="isLoading" />
        </form>
    </section>
</template>

<script>
// 3rd party
import axios from 'axios';

// Store
import {
    mapGetters,
    mapActions,
} from 'vuex';

// Local Modules
import localEpicTypes from '@webapp/epics/types.js';
import localStoreTypes from '@webapp/store/types.js';
import {
    TUTORIAL_NAMES,
} from '@webapp/store/modules/tutorial';

// Types
import Regulation from '@shared/types/regulation';
import {
    CountryIso2,
} from '@shared/types/registration';

// Shared Modules
import sharedEpicTypes from '@shared/epics/types.js';
import sharedStoreTypes from '@shared/store/types.js';
import {
    EventNames,
    AdjustParameterKeys,
} from '@shared/utils/analytics-keys';
import {
    getLocaleByCountryCode,
} from '@shared/utils/registration';
import {
    AgreementTypeName,
} from '@shared/modules/customer-first/types/agreement';

// Helpers
import apiUrl from '@webapp/helpers/api-url.js';
import {
    getUserAgreementLink,
} from '@shared/utils/user-agreement';

// Components
import email from '@webapp/components/login-process/email.vue';
import username from '@webapp/components/login-process/username.vue';
import dateOfBirth from '@webapp/components/login-process/date-of-birth.vue';
import countryStateDropdown from '@webapp/components/login-process/country-state.vue';
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';
import translationString from '@webapp/components/translation-string.vue';

// Config
import {
    ServerConfigurationProps,
} from '@shared/types/config';

const AgreementTypePerRegulation = {
    [Regulation.International]: AgreementTypeName.International,
    [Regulation.RequiresConfirmation]: AgreementTypeName.International,
    [Regulation.Usa]: AgreementTypeName.USA,
};

export default {
    name: 'register-widget',
    components: {
        email,
        username,
        dateOfBirth,
        countryStateDropdown,
        loadingSpinner,
        translationString,
    },
    data() {
        return {
            isLoading: false,
            form: {
                isTermsCheckboxTicked: false,
                isMailingCheckboxTicked: false,
            },
            selectedCountry: {
                canRegister: false,
                hasStates: false,
                country: {},
                state: {},
            },
            prepopulatedUserInfo: {
                username: '',
                email: '',
                dob: '',
                country: '',
            },
            regulation: null,
        };
    },
    computed: {
        ...mapGetters([
            'isRegisterWidgetEnabled',
            'registrationDetails',
            'isUsernameValid',
            'isEmailValid',
            'isDOBValid',
            'locale',
            'localeISO2',
            'device',
            'isPartnerApp',
            'isPartnerLogin',
            'partnerLoginFailed',
            'userId',
            'partnerAppData',
        ]),

        ...mapGetters('countries', ['countriesList']),

        ...mapGetters('auth', ['getUserRegistrationRestrictions']),

        ...mapGetters('agreements', ['userAgreement']),

        ...mapGetters('serverConfig', ['serverConfigByKey']),

        isCountryCorrectlySet() {
            if (!this.selectedCountry.country || !Object.keys(this.selectedCountry.country).length) {
                return false;
            }

            if (this.selectedCountry.hasStates) {
                return Boolean(this.selectedCountry.country &&
                    Object.keys(this.selectedCountry.country).length &&
                    this.selectedCountry.canRegister &&
                    this.selectedCountry.state &&
                    Object.keys(this.selectedCountry.state).length);
            }

            return Boolean(this.selectedCountry.country &&
                Object.keys(this.selectedCountry.country).length &&
                this.selectedCountry.canRegister);
        },

        isFormValid() {
            return this.isUsernameValid &&
            this.isEmailValid &&
            this.isDOBValid &&
            this.form.isTermsCheckboxTicked &&
            this.isCountryCorrectlySet;
        },

        isButtonDisabled() {
            return !this.isFormValid || this.isLoading;
        },

        termsAndConditionsLink() {
            return getUserAgreementLink(this.userAgreement);
        },

        privacyPolicyLink() {
            return this.serverConfigByKey(ServerConfigurationProps.privacyPolicyLink);
        },

        isCheckboxDisabled() {
            return this.isLoading || !this.regulation;
        },
    },
    watch: {
        selectedCountry: 'getUserRegulation',

        partnerLoginFailed(value) {
            if (value) {
                this.showAppLoader({
                    isVisible: false,
                });
                this.$router.push('/login');
                this.$notify({
                    type: 'error',
                    text: this.$t('registerMobileNumber.loginFailure'),
                });
            }
        },

        getUserRegistrationRestrictions(newRegulation) {
            this.regulation = newRegulation;
        },

        regulation() {
            const agreementType = AgreementTypePerRegulation[this.regulation];

            this.fetchUserAgreement({
                details: ['agreementVersionTranslations'],
                locales: [this.localeISO2],
                agreementType,
            });
        },
    },
    created() {
        this.checkForPrepopulatedUserInfo();
    },
    mounted() {
        if (this.registrationDetails?.countryCode === CountryIso2.USA) {
            this.setUsaAsSelectedCountry();
        }
    },
    methods: {
        ...mapActions({
            setUserSession: sharedStoreTypes.SET_USER_AUTH,
            initApp: localEpicTypes.INIT_START,
            showAppLoader: localStoreTypes.SET_APP_LOADER,
            doAppDataRefresh: sharedEpicTypes.REFRESH_APP_DATA,
            resetModal: localStoreTypes.RESET_MODAL,
            setModal: localStoreTypes.SET_MODAL,
            setRegistration: localStoreTypes.SET_REGISTRATION,
        }),

        ...mapActions(['detectPartnerApp', 'getPartnerResponse']),

        ...mapActions('tutorial', ['toggleTutorial']),

        ...mapActions('auth', ['fetchUserRegistrationRestrictions']),

        ...mapActions('agreements', ['fetchUserAgreement']),

        ...mapActions('serverConfig', ['fetchServerConfig']),

        async getUserRegulation() {
            await this.fetchUserRegistrationRestrictions({
                countryId: this.selectedCountry.country.id,
                mobileNumber: this.registrationDetails.formattedPhoneNumber,
            });

            if (Object.keys(this.partnerAppData).length) {
                const organization = this.partnerAppData.id;

                await this.fetchServerConfig({
                    organization,
                });
            } else {
                await this.fetchServerConfig();
            }


        },

        async checkRegulation() {
            if (this.regulation === Regulation.RequiresConfirmation) {
                this.openConfirmRegulationModal();
            } else {
                await this.register();
            }
        },

        setUsaAsSelectedCountry() {
            this.$refs.countryStateInput.onCountryChanged(this.countriesList.find((country) => country.iso2 === CountryIso2.USA));
        },

        async openConfirmRegulationModal() {
            await this.setModal({
                isVisible: true,
                image: require('@webapp/assets/globe.webp'),
                title: this.$t('registration.usaConfirmation.title'),
                messages: [
                    [
                        {
                            text: this.$t('registration.usaConfirmation.description'),
                        },
                    ],
                ],
                acceptButton: {
                    text: this.$t('registration.usaConfirmation.livingInUSA'),
                    action: () => {
                        this.regulation = Regulation.Usa;
                        this.setUsaAsSelectedCountry();
                        this.resetModal();
                    },
                },
                cancelButton: {
                    text: this.$t('registration.usaConfirmation.notLivingInUSA'),
                    action: () => {
                        this.regulation = Regulation.International;
                        this.register();
                        this.resetModal();
                    },
                },
            });
        },

        async register() {
            if (this.isButtonDisabled) {
                return;
            }

            this.isLoading = true;

            this.setRegistration([
                {
                    property: 'regulation',
                    value: this.regulation,
                },
            ]);

            const registrationData = {};
            const affiliation = sessionStorage.getItem('partnerRegistration');

            if (affiliation) {
                registrationData.affiliation = JSON.parse(affiliation);
            }

            try {
                let registration = await axios.post(`${apiUrl()}/registration/user`, {
                    username: this.registrationDetails.username,
                    email: this.registrationDetails.email,
                    birthDate: this.registrationDetails.dateOfBirth,
                    mobileNumber: this.registrationDetails.formattedPhoneNumber,
                    validationCode: this.registrationDetails.registrationCode,
                    locale: getLocaleByCountryCode({
                        iso2CountryCode: this.registrationDetails.countryIso2,
                        locale: this.locale,
                    }),
                    deviceUid: this.device.uuid,
                    deviceName: this.device.name,
                    registration: registrationData,
                    vatCountry: this.registrationDetails.countryIso2,
                    vatState: this.registrationDetails.stateCode,
                    regulation: this.registrationDetails.regulation,
                    userMetadata: {
                        marketingConsent: this.form.isMailingCheckboxTicked,
                    },
                });

                registration = registration.data.data;

                await this.setUserSession(registration);

                // Fetch User-related data
                this.showAppLoader({
                    isVisible: true,
                });

                if (this.isPartnerLogin) {
                    this.getPartnerResponse();
                } else {
                    await this.detectPartnerApp();
                    await this.initApp();
                    await this.doAppDataRefresh();

                    if (Object.keys(this.partnerAppData).length) {
                        const organization = this.partnerAppData.id;

                        await this.fetchServerConfig({
                            organization,
                        });
                    } else {
                        await this.fetchServerConfig();
                    }

                    if (this.isPartnerApp) {
                        this.$webTrackingInstance?.sendEvent(
                            EventNames.PartnerRegistration,
                            [
                                {
                                    key: 'partner',
                                    value: this.partnerAppData.id,
                                },
                                {
                                    key: 'user',
                                    value: this.userId,
                                },
                            ]
                        );
                    }

                    // New users don't need to see new feature tutorials
                    this.markTutorialsAsSeenForNewUsers();

                    this.$router.push('/home');
                }
                this.$webTrackingInstance?.sendEvent(EventNames.SignUpEnds);

                // Trigger Adjust tracking event
                this.$webTrackingInstance?.sendEvent(
                    EventNames.UserRegistration,
                    [
                        {
                            key: AdjustParameterKeys.USER_ID,
                            value: this.userId,
                        },
                    ]
                );

            } catch (error) {
                const fetchErrors = this.utilGlobalHandleErrorMessages(error);

                this.$notify({
                    type: 'error',
                    text: fetchErrors.message,
                });
            } finally {
                this.isLoading = false;
            }
        },
        markTutorialsAsSeenForNewUsers() {
            this.toggleTutorial({
                tutorialName: TUTORIAL_NAMES.LEADERBOARDS_INTRO_TUTORIAL,
                value: true,
            });

            this.toggleTutorial({
                tutorialName: TUTORIAL_NAMES.FAN_SHOP_INTRO_TUTORIAL,
                value: true,
            });
        },
        checkForPrepopulatedUserInfo() {
            if (window.location.search) {
                const userData = new URLSearchParams(window.location.search).get('userInfo') || '';

                if (userData) {
                    const decodedUserData = JSON.parse(decodeURIComponent(atob(userData)));

                    this.prepopulatedUserInfo.email = decodedUserData.email;
                    this.prepopulatedUserInfo.username = decodedUserData.username;
                    this.prepopulatedUserInfo.country = decodedUserData.country;
                    this.prepopulatedUserInfo.dob = (decodedUserData.dob) ? new Date(decodedUserData.dob).toISOString()
                        .split('T')[0] : '';
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.widget {
    background-color: $white-color;
}

.register__widget-row {
    &:not(:first-of-type) {
        margin-top: 4rem;
    }
}

.register-widget-link--disabled {
    pointer-events: none;
}

.label {
    margin: 6rem 0 4rem;
}

.warning {
    &__age-restrictions {
        margin: 2.5rem 0;
    }

    &__incl-icon {
        display: grid;
        grid-template-columns: 25px 1fr;
        grid-gap: 10px;
    }

    &__icon {
        color: $orange-peel-color;
        font-size: 25px;
    }

    &__text {
        font-size: 14px;
        line-height: 18px;
        margin: 0;
    }
}
</style>